import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';
import type { Rsvp, RsvpSummary, RsvpAnalytics, ListRsvpsQuery, SubmitRsvpPayload, RsvpListResponse } from './types';

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function authedRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new ApiError(message, res.status);
  }

  // Backend wraps every response in a `{ success, data, timestamp }` envelope.
  // Unwrap `data` here (mirroring the shared `request()` helper) so callers
  // receive the typed payload directly instead of the envelope.
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

/** Public — guest submits their RSVP. No auth required. */
export async function submitRsvpPublic(
  invitationId: string,
  payload: SubmitRsvpPayload,
): Promise<Rsvp> {
  const res = await fetch(`${API_BASE}/rsvp/${invitationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new ApiError(message, res.status);
  }

  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as Rsvp;
}

/** Authenticated owner — full RSVP list for one invitation. */
export function getRsvps(invitationId: string): Promise<Rsvp[]> {
  return authedRequest<Rsvp[]>(`${API_BASE}/rsvp/${invitationId}`);
}

/** Authenticated owner — aggregate counts (accepted/declined/maybe + headcount totals). */
export function getRsvpSummary(invitationId: string): Promise<RsvpSummary> {
  return authedRequest<RsvpSummary>(`${API_BASE}/rsvp/${invitationId}/summary`);
}

/** Authenticated owner — rich analytics: rates, forecast, time-series. */
export function getRsvpAnalytics(invitationId: string): Promise<RsvpAnalytics> {
  return authedRequest<RsvpAnalytics>(`${API_BASE}/rsvp/${invitationId}/analytics`);
}

/** Authenticated owner — search / filter / sort RSVPs. */
export function searchRsvps(invitationId: string, query: ListRsvpsQuery): Promise<RsvpListResponse> {
  const params = new URLSearchParams();
  if (query.page)   params.set('page', String(query.page));
  if (query.limit)  params.set('limit', String(query.limit));
  if (query.search) params.set('search', query.search);
  if (query.status && query.status !== 'ALL') params.set('status', query.status);
  if (query.sort)   params.set('sort', query.sort);
  const qs = params.toString();
  return authedRequest<RsvpListResponse>(`${API_BASE}/rsvp/${invitationId}/search${qs ? `?${qs}` : ''}`);
}

/** Authenticated owner — download RSVPs as CSV. Returns the raw text. */
export async function exportRsvpsCsv(invitationId: string): Promise<string> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const { auth } = await import('@/src/infrastructure/config/firebase');
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}/rsvp/${invitationId}/export/csv`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.text();
}

/** Authenticated owner — download RSVPs as Excel. Returns a Blob. */
export async function exportRsvpsExcel(invitationId: string): Promise<Blob> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const { auth } = await import('@/src/infrastructure/config/firebase');
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}/rsvp/${invitationId}/export/excel`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}
